import { getToken, clearToken } from '../utils/authToken';
/**
 * WorkspaceImageViewer.jsx - Vista Principal del Área de Trabajo
 * 
 * Estructura visual:
 * - Zona central: imagen activa, grande, centrada
 * - Barra lateral derecha: variantes (IMAGE_VARIANT)
 * - Botones: Guardar, Descargar, Imprimir, Imágenes
 * - "X" para cerrar imagen activa
 * - AOTI: Auto-Optimización Inteligente de Tamaño de Imagen
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Save, Download, Printer, Image, X, 
  ChevronLeft, Trash2, Pencil,
  ZoomIn, ZoomOut, Maximize2,
  CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageEditor from './ImageEditor';
import { useLanguage } from '../context/LanguageContext';
import { analyzeImage, optimizeImage } from '../services/AOTIService';
import { saveBlobWithPicker } from '../utils/downloadBlob';

import { BACKEND_URL as API_URL } from '../config/api';

const WorkspaceImageViewer = ({
  workspaceItem,
  variants = [],
  onSelectVariant,
  onDeleteItem,
  onClose,
  onRefresh,
  onOpenGallery,
  onSelectFromGallery,
  language: propLanguage
}) => {
  const { t: globalT, language: contextLanguage } = useLanguage();
  const language = propLanguage || contextLanguage;
  
  // CUADRO NEGRO — Zoom anti-flash + límites reales (feb-2026, FIX FINAL).
  // Spec del usuario:
  //   - MIN_ZOOM=0.1 (10%), MAX_ZOOM=4 (400%)
  //   - auto-fit calculado UNA SOLA VEZ en onLoad, leyendo naturalWidth/Height
  //   - <img> oculto (opacity:0) hasta que loaded=true → cero flash
  //   - sin pre-decode, sin fallback inline (causaba dims inconsistentes)
  //   - sin ResizeObserver auto-recalculando zoom
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 4;
  const AUTOFIT_MARGIN = 0.95;

  const [showEditor, setShowEditor] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const imageContainerRef = useRef(null);
  const printRef = useRef(null);
  const loadedImgRef = useRef(null);

  // --- AOTI State ---
  const [aotiAnalysis, setAotiAnalysis] = useState(null);
  const [aotiConsent, setAotiConsent] = useState(null); // null = sin decidir, true = aceptó, false = rechazó
  const [showAOTIPrompt, setShowAOTIPrompt] = useState(false);
  const [aotiOptimizedBase64, setAotiOptimizedBase64] = useState(null);
  const [aotiDimensions, setAotiDimensions] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Usar traducciones del contexto
  const t = (key) => globalT(`imageWorkspace.${key}`);

  // Cargar imagen activa
  useEffect(() => {
    if (workspaceItem) {
      setActiveImage(workspaceItem);
      // Reset COMPLETO: hasta que el nuevo <img> dispare onLoad no mostramos
      // nada y el zoom se recalcula desde cero (auto-fit basado en las dims
      // reales de la imagen, no de un thumbnail).
      setLoaded(false);
      setImgSize({ w: 0, h: 0 });
      setZoom(1);
      // Reset AOTI state when image changes
      setAotiAnalysis(null);
      setAotiConsent(null);
      setShowAOTIPrompt(false);
      setAotiOptimizedBase64(null);
      setAotiDimensions(null);

      // Vista Completa Historial → si la imagen llega con un `history_action`
      // (Motor Pro, Variantes Pro, Generar, Fondos), abrir
      // el editor automaticamente. Asi el usuario no tiene que hacer doble
      // click; cae directo al modulo correspondiente con la herramienta
      // activa segun la accion (handled en ImageEditor mediante prop).
      if (workspaceItem?.history_action) {
        setShowEditor(true);
      }
      loadedImgRef.current = null;
    }
  }, [workspaceItem]);

  // ─── onLoad-driven auto-fit (FIX FINAL CUADRO NEGRO) ───
  // Spec del usuario: simple, sin pre-decode. El <img> se monta directo con
  // su URL pero invisible (opacity:0). Cuando dispara onLoad leemos
  // naturalWidth/Height REALES, calculamos autoFit UNA SOLA VEZ, seteamos
  // zoom + imgSize + loaded=true, y recién entonces aparece visible.
  //
  // El bug del fix anterior: el thumbnail-fallback pre-decodificaba dims
  // chicas y calculaba un autoFit ~0.95; al llegar la URL final con dims
  // grandes (4000x3000) el wrapper quedaba en 4000*0.95 = 3800px (gigante).
  // Eliminado fallback + pre-decode → autoFit siempre usa dims reales.

  const computeAutoFit = useCallback((w, h) => {
    const container = imageContainerRef.current;
    if (!container || w <= 0 || h <= 0) return 1;
    const cw = container.clientWidth - 48;
    const ch = container.clientHeight - 48;
    if (cw <= 0 || ch <= 0) return 1;
    const fit = Math.min(cw / w, ch / h, 1) * AUTOFIT_MARGIN;
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fit));
  }, [MIN_ZOOM, MAX_ZOOM, AUTOFIT_MARGIN]);

  // --- AOTI: Analizar imagen cuando carga + setear auto-fit UNA SOLA VEZ ---
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    loadedImgRef.current = img;
    const w = img.naturalWidth || img.width || 0;
    const h = img.naturalHeight || img.height || 0;

    if (w > 0 && h > 0) {
      const analysis = analyzeImage(w, h);
      setAotiAnalysis(analysis);
      const autoFit = computeAutoFit(w, h);
      setImgSize({ w, h });
      setZoom(Math.round(autoFit * 100) / 100);
    }
    // loaded=true al final → activa el fade-in del wrapper y monta visible.
    setLoaded(true);
  }, [computeAutoFit]);

  // CUADRO NEGRO anti-flash: el ResizeObserver que recalculaba zoom
  // automáticamente al hacer resize del contenedor fue ELIMINADO a propósito.
  // Spec del usuario: "el zoom NO se recalcula automáticamente después del
  // primer render. Solo se calcula auto-fit UNA VEZ al cargar la imagen".
  // Si el usuario quiere re-encajar puede usar el botón "fit to screen".

  // --- AOTI: Aceptar optimización → optimizar + abrir editor inmediatamente ---
  const handleAOTIAccept = useCallback(async () => {
    setAotiConsent(true);
    setShowAOTIPrompt(false);
    
    // Solo optimizar si realmente necesita redimensión
    if (aotiAnalysis?.needsOptimization) {
      setIsOptimizing(true);
      try {
        const img = loadedImgRef.current || new window.Image();
        if (!loadedImgRef.current) {
          const imgUrl = activeImage?.preview_url?.startsWith('/api')
            ? `${API_URL}${activeImage.preview_url}`
            : activeImage?.preview_url;
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imgUrl;
          });
        }
        const optimized = await optimizeImage(img);
        setAotiOptimizedBase64(optimized.dataUrl);
        setAotiDimensions({ width: optimized.width, height: optimized.height });
      } catch (err) {
        console.error('AOTI optimization failed:', err);
        setAotiOptimizedBase64(null);
        setAotiDimensions(null);
      } finally {
        setIsOptimizing(false);
      }
    }
    setShowEditor(true);
  }, [activeImage, aotiAnalysis]);

  // --- AOTI: Rechazar optimización → abrir editor, no volver a preguntar ---
  const handleAOTIReject = useCallback(() => {
    setAotiConsent(false);
    setShowAOTIPrompt(false);
    setShowEditor(true);
  }, []);

  // --- AOTI: Doble click — SIEMPRE muestra flash si no ha decidido ---
  const handleDoubleClick = useCallback(async () => {
    // Si no ha decidido aún → mostrar flash (SIEMPRE, sin condición de tamaño)
    if (aotiConsent === null) {
      setShowAOTIPrompt(true);
      return;
    }

    // Ya decidió antes — abrir editor directamente
    if (aotiConsent === true && aotiAnalysis?.needsOptimization) {
      setIsOptimizing(true);
      try {
        const img = loadedImgRef.current || new window.Image();
        if (!loadedImgRef.current) {
          const imgUrl = activeImage?.preview_url?.startsWith('/api')
            ? `${API_URL}${activeImage.preview_url}`
            : activeImage?.preview_url;
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imgUrl;
          });
        }
        const optimized = await optimizeImage(img);
        setAotiOptimizedBase64(optimized.dataUrl);
        setAotiDimensions({ width: optimized.width, height: optimized.height });
      } catch (err) {
        console.error('AOTI optimization failed:', err);
        setAotiOptimizedBase64(null);
        setAotiDimensions(null);
      } finally {
        setIsOptimizing(false);
      }
    }
    setShowEditor(true);
  }, [aotiAnalysis, aotiConsent, activeImage]);

  // Toggle "Ajustar a pantalla" ↔ "Tamaño real (100%)".
  // Si el zoom actual está dentro del 5% del fit calculado (osea ya
  // está ajustado a pantalla), pasamos a 100% (tamaño real). Si está
  // en cualquier otro nivel, lo encajamos a pantalla. Esto le da al
  // botón un efecto visible siempre que el usuario hace clic.
  const handleFitToScreen = useCallback(() => {
    const container = imageContainerRef.current;
    const img = loadedImgRef.current;
    if (!container || !img) {
      setZoom(1);
      return;
    }
    const containerW = container.clientWidth - 64; // padding
    const containerH = container.clientHeight - 64;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    if (imgW <= 0 || imgH <= 0) {
      setZoom(1);
      return;
    }
    const scaleX = containerW / imgW;
    const scaleY = containerH / imgH;
    const fitZoom = Math.min(scaleX, scaleY, 1) * AUTOFIT_MARGIN;
    const clampedFit = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
    const rounded = Math.round(clampedFit * 100) / 100;

    // Si el zoom actual ya está cerca del fit (±5%), saltamos a 100%
    // (tamaño real). Caso contrario aplicamos el fit.
    setZoom((prev) => {
      const current = prev ?? 1;
      const nearFit = Math.abs(current - rounded) < 0.05;
      const next = nearFit ? 1 : rounded;
      return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    });
  }, [AUTOFIT_MARGIN, MIN_ZOOM, MAX_ZOOM]);

  // Guardar imagen
  const handleSave = async () => {
    if (!activeImage?.id) return;
    
    setIsSaving(true);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/workspace/items/${activeImage.id}/save`,
        {},
        {}
      );
      toast.success(t('saved'));
      // NO llamar a onRefresh() aquí - la imagen debe permanecer visible
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(t('errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  // Descargar imagen
  const handleDownload = async () => {
    if (!activeImage?.id) return;

    try {
      const token = getToken();
      let blob = null;
      let filename = `${activeImage.display_name || 'imagen'}.png`;

      // 1. Intentar obtener la imagen con base64 del backend
      try {
        const response = await axios.get(
          `${API_URL}/api/workspace/items/${activeImage.id}/download`,
          {}
        );

        if (response.data.success && response.data.base64 && response.data.base64.length > 100) {
          let base64Data = response.data.base64;
          if (base64Data.startsWith('data:')) {
            base64Data = base64Data.split(',')[1];
          }
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], { type: 'image/png' });
          filename = response.data.filename || filename;
        }
      } catch (_backendError) { /* fallback */ }

      // 2. Fallback: descargar desde preview_url
      if (!blob) {
        const imageUrl = activeImage.preview_url?.startsWith('/api')
          ? `${API_URL}${activeImage.preview_url}`
          : activeImage.preview_url;

        if (!imageUrl) {
          toast.error(t('errorDownloading'));
          return;
        }

        const response = await fetch(imageUrl, {});
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        blob = await response.blob();
        if (blob.size < 100) throw new Error('Image data too small');
      }

      // 3. Estrategia universal: abrir en pestana nueva. En caso que el popup
      // este bloqueado, saveBlobWithPicker cae a <a download>.
      const result = await saveBlobWithPicker(blob, filename, 'image/png');
      if (result === 'fallback') {
        toast.success(
          language === 'es'
            ? 'Imagen descargada en tu carpeta Descargas'
            : 'Image downloaded to your Downloads folder'
        );
      } else {
        toast.success(
          language === 'es'
            ? 'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir la carpeta.'
            : 'Image opened in a new tab. Right-click → "Save image as…" to choose the folder.',
          { duration: 7000 }
        );
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(t('errorDownloading'));
    }
  };

  // Imprimir imagen
  const handlePrint = async () => {
    if (!activeImage?.id) return;

    try {
      const token = getToken();
      let blob = null;

      // 1. Intentar obtener la imagen con base64 del backend (mismo flujo que download)
      try {
        const response = await axios.get(
          `${API_URL}/api/workspace/items/${activeImage.id}/download`,
          {}
        );
        if (response.data.success && response.data.base64 && response.data.base64.length > 100) {
          let b64 = response.data.base64;
          if (b64.startsWith('data:')) b64 = b64.split(',')[1];
          const bytes = atob(b64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          blob = new Blob([arr], { type: 'image/png' });
        }
      } catch (_e) { /* fallback */ }

      // 2. Fallback: descargar desde preview_url
      if (!blob) {
        const imageUrl = activeImage.preview_url?.startsWith('/api')
          ? `${API_URL}${activeImage.preview_url}`
          : activeImage.preview_url;
        if (!imageUrl) {
          toast.error(globalT('imageWorkspace.printError') || 'Error al imprimir');
          return;
        }
        const resp = await fetch(imageUrl, {});
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        blob = await resp.blob();
      }

      // 3. Convertir blob a data URL para embeberla directamente (sin depender
      // de tokens ni cross-origin en la nueva pestana)
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // 4. Abrir ventana nueva. En iframe de preview puede ser bloqueada.
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error(globalT('imageWorkspace.popupBlocked') || 'Popup bloqueado. Permite popups para imprimir.');
        return;
      }

      const title = activeImage?.display_name || 'DamyxIA Image';
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${title.replace(/</g, '&lt;')}</title>
  <style>
    @page { margin: 10mm; }
    @media print {
      body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
      img { max-width: 100%; max-height: 100vh; object-fit: contain; }
    }
    body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; font-family: system-ui; }
    img { max-width: 100%; max-height: 90vh; object-fit: contain; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
  </style>
</head>
<body>
  <img src="${dataUrl}" alt="${title.replace(/"/g, '&quot;')}" />
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () {
        try { window.print(); } catch (e) { console.error(e); }
      }, 300);
    });
  </script>
</body>
</html>`);
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing:', error);
      toast.error(globalT('imageWorkspace.printError') || 'Error al imprimir');
    }
  };

  // Eliminar elemento
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const handleDelete = () => {
    if (!activeImage?.id) {
      toast.error(globalT('imageWorkspace.noImageToDelete'));
      return;
    }
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/workspace/items/${activeImage.id}`,
        {}
      );
      toast.success(globalT('imageWorkspace.imageDeleted'));
      
      // Auto-refresh badge de la Papelera.
      window.dispatchEvent(new CustomEvent('damyxia:trash-updated'));

      // Notificar al componente padre
      if (onDeleteItem) onDeleteItem(activeImage.id);
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(globalT('imageWorkspace.errorDeleted'));
    }
  };

  // Manejar creación de variante desde el editor
  // 🟥 CUADRO NEGRO 2026-07-25 P12.2 — FIX: cuando se crea una variante, el editor
  // seguía mostrando la imagen ORIGINAL porque `activeImage` no se actualizaba.
  // Marcelo reportaba "toast verde pero imagen no cambia". Ahora reemplazamos
  // activeImage por la variante recién creada — el <ImageEditor> monta con
  // workspaceItem/imageUrl nuevos y el canvas muestra la variante real.
  const handleVariantCreated = (newVariant) => {
    if (!newVariant) {
      if (onRefresh) onRefresh();
      return;
    }
    // Aseguramos que preview_url apunta al backend correcto para que <img> pueda
    // cargarlo sin problemas (mismo tratamiento que activeImage inicial).
    setActiveImage(newVariant);
    // Reset del canvas para que el useEffect de load haga fit auto de la nueva.
    setLoaded(false);
    setImgSize({ w: 0, h: 0 });
    setZoom(1);
    setAotiOptimizedBase64(null);
    setAotiAnalysis(null);
    setAotiConsent(null);
    setAotiDimensions(null);
    if (onRefresh) onRefresh();
  };

  // Cerrar imagen activa (sin borrarla)
  const handleCloseActiveImage = () => {
    if (onClose) {
      onClose();
    }
  };

  // Si está en modo editor
  if (showEditor && activeImage) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-zinc-800 border-b border-zinc-700">
          <button
            onClick={() => setShowEditor(false)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
                        {globalT('imageWorkspace.back')}
          </button>
          <span className="text-sm text-white font-medium truncate max-w-[300px]">
            {activeImage.display_name}
          </span>
          <div className="w-24" />
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <ImageEditor
            key={activeImage.id}
            workspaceItem={activeImage}
            imageUrl={aotiOptimizedBase64 ? undefined : activeImage.preview_url}
            imageBase64={aotiOptimizedBase64 || activeImage.base64}
            onSave={handleSave}
            onCreateVariant={handleVariantCreated}
            onClose={() => { setShowEditor(false); setAotiOptimizedBase64(null); }}
            language={language}
            aotiData={aotiDimensions ? { optimized: true, dimensions: aotiDimensions } : (aotiConsent === false && aotiAnalysis?.needsOptimization ? { optimized: false, dimensions: null } : null)}
          />
        </div>
      </div>
    );
  }

  const imageUrl = activeImage?.preview_url?.startsWith('/api')
    ? `${API_URL}${activeImage.preview_url}`
    : activeImage?.preview_url;

  return (
    <div className="h-full flex flex-col bg-zinc-100 dark:bg-zinc-900">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="workspace-delete-confirm">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{globalT('imageWorkspace.confirmDeleteImage') || 'Eliminar imagen?'}</h3>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors">
                {globalT('cancel') || 'Cancelar'}
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors" data-testid="confirm-workspace-delete">
                {globalT('delete') || 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Zona central única - Imagen activa */}
      <div className="flex-1 flex flex-col">
        {/* Header con botones principales - RESPONSIVE para móvil */}
        <div className="flex-shrink-0 bg-zinc-800 border-b border-zinc-700">
          {/* Fila superior: Info + Botón cerrar (siempre visible) */}
          <div className="flex items-center justify-between px-2 sm:px-4 py-2">
            {/* Info de la imagen - compacta en móvil */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                activeImage?.type === 'IMAGE_SOURCE' 
                  ? 'bg-emerald-900/30' 
                  : 'bg-purple-900/30'
              }`}>
                <Image className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  activeImage?.type === 'IMAGE_SOURCE' 
                    ? 'text-emerald-400' 
                    : 'text-purple-400'
                }`} />
              </div>
              <div className="min-w-0">
                <h3 className="font-medium text-white text-xs sm:text-sm truncate">
                  {activeImage?.display_name || 'Sin nombre'}
                </h3>
                <span className={`text-[10px] sm:text-xs ${
                  activeImage?.type === 'IMAGE_SOURCE' 
                    ? 'text-emerald-400' 
                    : 'text-purple-400'
                }`}>
                  {activeImage?.type === 'IMAGE_SOURCE' ? t('imageSource') : t('imageVariant')}
                </span>
              </div>
            </div>
            
            {/* Botones de acción - SIEMPRE visibles */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-1.5 sm:p-2 text-emerald-400 hover:bg-emerald-900/20 rounded transition-colors disabled:opacity-50"
                title={globalT('imageWorkspace.save')}
                data-testid="save-image-btn"
              >
                <Save className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={handleDownload}
                className="p-1.5 sm:p-2 text-blue-400 hover:bg-blue-900/20 rounded transition-colors"
                title={globalT('imageWorkspace.download')}
                data-testid="download-image-btn"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={handlePrint}
                className="hidden sm:block p-1.5 sm:p-2 text-zinc-400 hover:bg-zinc-700/50 rounded transition-colors"
                title={globalT('imageWorkspace.print')}
                data-testid="print-image-btn"
              >
                <Printer className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-1.5 sm:p-2 text-red-400 hover:bg-red-900/20 rounded transition-colors"
                title={globalT('imageWorkspace.delete')}
                data-testid="delete-image-btn"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="w-px h-5 bg-zinc-600 mx-0.5 sm:mx-1" />
              <button
                onClick={handleCloseActiveImage}
                className="p-1.5 sm:p-2 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors"
                title={globalT('imageWorkspace.closeImage')}
                data-testid="close-active-image-btn"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>
          
          {/* Fila de zoom - opcional en móvil */}
          <div className="flex items-center justify-center gap-1 px-2 py-1 bg-zinc-700/30 border-t border-zinc-700/50">
            <button
              onClick={() => setZoom(prev => Math.max(MIN_ZOOM, (prev ?? 1) - 0.25))}
              className="p-1 text-zinc-400 hover:text-white rounded transition-colors"
              title={t('zoomOut')}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-zinc-400 min-w-[3rem] text-center">
              {Math.round((zoom ?? 1) * 100)}%
            </span>
            <button
              onClick={() => setZoom(prev => Math.min(MAX_ZOOM, (prev ?? 1) + 0.25))}
              className="p-1 text-zinc-400 hover:text-white rounded transition-colors"
              title={t('zoomIn')}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleFitToScreen}
              className="p-1 text-zinc-400 hover:text-white rounded transition-colors"
              title={language === 'es' ? 'Ajustar a pantalla / Tamaño real (100%)' : 'Fit to screen / Actual size (100%)'}
              data-testid="fit-to-screen-btn"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Área de imagen */}
        <div 
          ref={imageContainerRef}
          className="flex-1 min-h-0 overflow-auto relative"
          style={{
            backgroundColor: '#1a1a2e',
            backgroundImage: 'linear-gradient(45deg, #1e1e32 25%, transparent 25%), linear-gradient(-45deg, #1e1e32 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e1e32 75%), linear-gradient(-45deg, transparent 75%, #1e1e32 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px'
          }}
        >
          {/* Centering wrapper - works with overflow-auto */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', minWidth: '100%', padding: '16px', boxSizing: 'border-box' }}>
          {imageUrl ? (
            <div 
              ref={printRef}
              className="relative cursor-pointer group border border-[#DADADA]"
              style={{ 
                // CUADRO NEGRO FIX FINAL: el wrapper toma tamaño real recién
                // cuando loaded=true (es decir, onLoad calculó imgSize+autoFit).
                // Mientras tanto reservamos un placeholder estable para que
                // el layout no salte ni se vea la imagen en tamaño natural.
                width: loaded
                  ? Math.round(imgSize.w * zoom) + 'px'
                  : 'min(480px, 80vw)',
                height: loaded
                  ? Math.round(imgSize.h * zoom) + 'px'
                  : 'min(480px, 60vh)',
                flexShrink: 0,
                boxShadow: '0 0 6px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                backgroundColor: loaded ? 'transparent' : 'rgba(39, 39, 42, 0.6)',
                transition: 'background-color 120ms ease-out'
              }}
              onDoubleClick={handleDoubleClick}
            >
              {/* CUADRO NEGRO FIX FINAL: el <img> está SIEMPRE en el DOM para
                  que el browser inicie la descarga, pero permanece invisible
                  (opacity:0) hasta loaded=true. onLoad calcula naturalDims +
                  autoFit ANTES de revelarlo → nunca se ve tamaño natural. */}
              <img
                src={imageUrl}
                alt={activeImage?.display_name || 'Workspace image'}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  opacity: loaded ? 1 : 0,
                  transition: 'opacity 120ms ease-out',
                }}
                onLoad={handleImageLoad}
                onError={(e) => {
                  console.error('Error loading image');
                  e.target.style.display = 'none';
                }}
              />
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-200 dark:bg-zinc-700">
                  <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              
              {/* AOTI: Estado "optimización en progreso" */}
              {isOptimizing && (
                <div className="absolute inset-0 flex items-center justify-center z-40 bg-black/50 backdrop-blur-sm" data-testid="aoti-optimizing-overlay">
                  <div className="flex flex-col items-center gap-3 bg-zinc-900 border border-zinc-600 rounded-2xl shadow-2xl px-8 py-6">
                    <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                    <span className="text-sm text-zinc-200 font-medium">Optimizaci&oacute;n en progreso...</span>
                  </div>
                </div>
              )}
              
              {/* Botón informativo - Doble clic para editar */}
              {loaded && !showEditor && !showAOTIPrompt && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    className="flex items-center gap-2 px-4 py-2 bg-zinc-800/80 backdrop-blur-sm border border-zinc-600 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <Pencil className="w-4 h-4 text-zinc-600 dark:text-zinc-300" />
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                      {globalT('imageWorkspace.doubleClickToEdit')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-zinc-400">
              <Image className="w-16 h-16 mb-4 opacity-30" />
              <span className="text-sm">
                {globalT('imageWorkspace.noImageSelected')}
              </span>
            </div>
          )}
          </div>

          {/* AOTI Flash Message — fuera de printRef, dentro de imageContainerRef */}
          {showAOTIPrompt && (
            <div
              data-testid="aoti-flash-prompt"
              className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-zinc-900/95 backdrop-blur border border-zinc-600 rounded-xl shadow-2xl"
              style={{ maxWidth: '420px' }}
            >
              <span className="text-xs text-zinc-200 leading-snug">
                Editor de Imagen con IA para mejor Resultados
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  data-testid="aoti-accept-btn"
                  onClick={handleAOTIAccept}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Si
                </button>
                <button
                  data-testid="aoti-reject-btn"
                  onClick={handleAOTIReject}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  No
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceImageViewer;
